import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { getEnabledChannels } from '@/lib/queries/channels'

interface ChannelWithIntegration {
  _id: string
  _type: 'channel'
  name: string
  activeIntegration?: boolean
  integrationIdentifier?: string
  rates?: Record<string, any>
}

/**
 * Channel selector page for import
 * Allows users to select which channel to import from
 * Based on wireframe: import-channel.html
 */
export default function ChannelSelector() {
  const navigate = useNavigate()

  const {
    data: channels,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['channels', 'enabled'],
    queryFn: getEnabledChannels,
  })

  // Filter to only show channels with active integrations, excluding Printify
  const importableChannels = (channels || []).filter(
    (channel: ChannelWithIntegration) =>
      channel.activeIntegration === true &&
      channel.integrationIdentifier &&
      channel.integrationIdentifier !== 'printify'
  ) as ChannelWithIntegration[]

  const handleChannelSelect = (identifier: string) => {
    navigate(`/import/${identifier}`)
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-gray-900">Import from Channel</h1>
        </div>
      </header>

      <main className="p-4 space-y-6">
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-base">
              1
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Select Channel</h2>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-600">Loading channels...</div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-semibold mb-1">Error loading channels</p>
              <p className="text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error occurred'}
              </p>
            </div>
          ) : importableChannels.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              <p className="mb-2">No importable channels found.</p>
              <p className="text-sm text-gray-500">
                Channels need to have an active integration configured to be available for import.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {importableChannels.map((channel) => (
                <button
                  key={channel._id}
                  type="button"
                  onClick={() => handleChannelSelect(channel.integrationIdentifier!)}
                  className="w-full bg-white border-2 border-gray-300 rounded-lg p-4 text-left hover:border-blue-600 hover:bg-blue-50 transition-colors touch-target-min-44"
                >
                  <div className="font-medium text-gray-900">{channel.name}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Click to import from {channel.name}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">How it works</h4>
          <p className="text-sm text-gray-700">
            Select a channel to import products and listings from. Each channel requires an active
            integration to be configured.
          </p>
        </div>
      </main>
    </div>
  )
}
